import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { fadeInUp, staggerContainer, scaleIn } from '../hooks/useScrollAnimation';
import AnimatedCard from './AnimatedCard';
import Button from './common/Button';
import { FORECAST_URL, BOOK_CALL_URL } from '../config/links';

const Hero = () => {
  return (
    <section className="relative flex flex-col items-center justify-center overflow-hidden bg-esf-black pt-28 pb-24 md:pt-36 md:pb-32">
      <div className="absolute inset-0 bg-radial-glow opacity-60" aria-hidden="true" />
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] max-w-full bg-radial-glow-purple opacity-40"
        aria-hidden="true"
      />

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center"
      >
        <motion.p variants={fadeInUp} className="text-eyebrow uppercase text-esf-blue mb-6">
          Fan Finance Platform
        </motion.p>

        <motion.h1
          variants={fadeInUp}
          className="text-display-xl text-esf-white text-balance"
        >
          Turn fan passion into{' '}
          <span className="text-gradient">club revenue</span>
        </motion.h1>

        <motion.p
          variants={fadeInUp}
          className="mt-6 text-body-lg md:text-xl text-esf-gray-300 max-w-2xl mx-auto text-pretty"
        >
          We build, fund and run a complete fan finance platform for your club —
          white-label cards, loyalty rewards and a shared merchant network. No cost
          to you, and no IT project on your side.
        </motion.p>

        {/*
          Tiered deliberately: the forecast tool is the low-commitment hook and
          gets the only primary treatment on the screen. Booking a call is the
          higher-intent path but a bigger ask, so it sits quieter here and
          becomes primary in the closing CTA, where intent is highest.
        */}
        <motion.div
          variants={fadeInUp}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
        >
          <Button href={FORECAST_URL} variant="primary" size="lg" className="w-full sm:w-auto">
            Estimate your revenue
            <ArrowRight size={18} aria-hidden="true" />
          </Button>
          <Button href={BOOK_CALL_URL} variant="secondary" size="lg" className="w-full sm:w-auto">
            Book a call
          </Button>
        </motion.div>

        <motion.p variants={fadeInUp} className="mt-5 text-body-sm text-esf-gray-500">
          Takes about 2 minutes. No commitment, no card details.
        </motion.p>
      </motion.div>

      <motion.div
        variants={scaleIn}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.6 }}
        className="relative z-10 mt-16 md:mt-20"
      >
        <AnimatedCard />
        <div
          className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-[400px] max-w-full h-[150px] bg-esf-blue/10 rounded-full blur-3xl -z-10"
          aria-hidden="true"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
